import { useLayoutEffect, useRef } from 'react';
import { C } from '@parsina/framework/tokens';

/**
 * The frame every mobile screen is built in.
 *
 * It exists to make the viewport contract (docs/MOBILE-REDESIGN.md §3.1) a
 * property of the frame rather than a rule each screen has to remember:
 *
 *   • ONE scroll region. The document does not scroll on a phone (`styles.css`
 *     locks it), and a nested second scroller inside a screen is a defect —
 *     it steals the gesture, and on iOS it traps `position: fixed` descendants
 *     (which is what clips a sheet mounted inside it).
 *   • The last card is never covered, and a docked CTA sits directly above the
 *     bottom nav rather than a bar's height away from it.
 *
 * ── Why this frame reserves NO space for the bottom nav ─────────────────────
 *
 * It used to, via `--app-bottom-nav` — the measured height the kit's `BottomNav`
 * publishes while it is mounted. That variable is for things that FLOAT OVER the
 * shell (a toast, an install card, a `position: fixed` CTA), and this frame is
 * not one of them: `ClinicLayout`'s phone branch is a flex column whose children
 * are [this screen][the nav], and the kit's bar is `position: relative` with
 * `flexShrink: 0`. The frame's box therefore ALREADY ends exactly where the bar
 * begins — its own docstring calls it "a fixed bar", which the style has not
 * been for some time.
 *
 * So adding the bar's height inside the frame reserved it twice, and the second
 * reservation was visible: «فراخوان بیمار بعدی» on پذیرش floated 72px above the
 * nav on a 390×844 phone — 12px of the CTA bar's own breathing room, and 60px of
 * bar height that nothing occupied. Every mobile list also carried 60px of dead
 * scroll slack under its last row.
 *
 * The safe-area inset is the same story with one twist: `BottomNav` already
 * applies `max(env(safe-area-inset-bottom), 0.25rem)` as its own bottom
 * padding, so when the bar is there the home indicator is already cleared and
 * adding the inset here pushes the CTA up a second time. When the bar is NOT
 * there — a drilled-into flow, or a person whose access yields no bottom-nav
 * destination at all — nothing below clears it and the frame must.
 *
 * `--app-bottom-nav` is exactly the signal for that, since the bar sets it on
 * mount and clears it on unmount, so `BOTTOM_INSET` subtracts the bar's height
 * from the inset and floors at zero: a 60px bar swallows a 34px indicator and
 * the term vanishes, no bar leaves the full inset. That is why it reads the
 * variable at all — to know whether a bar EXISTS, never to reserve its height.
 */

/**
 * The home indicator, minus whatever the bottom bar is already clearing.
 * See the note above; `hasNav={false}` forces the no-bar answer for a surface
 * that knows it is full-screen before the bar has unmounted.
 */
const BOTTOM_INSET = 'max(env(safe-area-inset-bottom, 0px) - var(--app-bottom-nav, 0px), 0px)';

/**
 * The docked CTA bar's height, published as `--app-screen-cta` while it is
 * mounted — the same contract the kit's BottomNav keeps with `--app-bottom-nav`.
 * Anything that floats above the bottom edge (the secretary's button) stacks on
 * it instead of covering the screen's one primary action.
 */
export const SCREEN_CTA_VAR = '--app-screen-cta';
/** Room the floating secretary button takes, published by it while shown (SecretaryFab). */
export const FAB_SPACE_VAR = '--app-fab-space';

function usePublishedHeight(ref, cssVar, active) {
  useLayoutEffect(() => {
    const root = typeof document !== 'undefined' ? document.documentElement : null;
    const el = ref.current;
    if (!root || !active || !el) return undefined;
    const publish = () => root.style.setProperty(cssVar, `${Math.round(el.getBoundingClientRect().height)}px`);
    publish();
    const watch = typeof ResizeObserver === 'function' ? new ResizeObserver(publish) : null;
    watch?.observe(el);
    return () => {
      watch?.disconnect();
      root.style.removeProperty(cssVar);
    };
  }, [ref, cssVar, active]);
}

export default function MobileScreen({
  header,
  children,
  /** The screen's single primary action, docked above the bottom nav. */
  cta,
  /** False for a drilled-into surface, which has no bottom nav. */
  hasNav = true,
  'data-state': dataState,
  'data-testid': testId,
}) {
  // Nothing for the bar's HEIGHT — it sits below this frame, not over it.
  const bottomInset = hasNav ? BOTTOM_INSET : 'env(safe-area-inset-bottom, 0px)';
  const ctaRef = useRef(null);
  usePublishedHeight(ctaRef, SCREEN_CTA_VAR, Boolean(cta));

  return (
    <div
      data-state={dataState}
      data-testid={testId}
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: C.bg,
        overflow: 'hidden',
      }}
    >
      {header}

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          // …plus the floating secretary button's room, so the last row can scroll
          // clear of it — its «⋮» sits exactly where the button floats.
          paddingBottom: `calc(1rem + ${bottomInset} + var(${FAB_SPACE_VAR}, 0px))`,
        }}
      >
        {children}
      </div>

      {cta && (
        <div
          ref={ctaRef}
          data-testid="mobile-screen-cta"
          style={{
            flexShrink: 0,
            padding: '0.75rem 1rem',
            paddingBottom: `calc(0.75rem + ${bottomInset})`,
            background: C.card,
            borderTop: `1px solid ${C.border}`,
          }}
        >
          {cta}
        </div>
      )}
    </div>
  );
}
